import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CommentItem from "./CommentItem";
const OVERSCAN = 3;
export default function CommentList({ comments, currentUserId, onEdit, onDelete, height = 480, }) {
    const containerRef = useRef(null);
    const [measurements, setMeasurements] = useState([]);
    const [scrollTop, setScrollTop] = useState(0);
    const [containerHeight, setContainerHeight] = useState(height);
    useEffect(() => {
        setMeasurements((previous) => comments.map((_, index) => {
            const existing = previous[index];
            return typeof existing === "number" ? existing : 180;
        }));
    }, [comments]);
    const handleScroll = useCallback((event) => {
        setScrollTop(event.currentTarget.scrollTop);
    }, []);
    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === containerRef.current) {
                    setContainerHeight(entry.contentRect.height);
                }
            }
        });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, []);
    const attachMeasurementRef = useCallback((index) => (node) => {
        if (!node)
            return;
        const rect = node.getBoundingClientRect();
        const heightValue = rect.height;
        if (heightValue <= 0) {
            return;
        }
        setMeasurements((previous) => {
            const next = [...previous];
            if (next[index] === heightValue) {
                return previous;
            }
            next[index] = heightValue;
            return next;
        });
    }, []);
    const { visibleItems, totalHeight, shouldVirtualize, } = useMemo(() => {
        const measurementArray = measurements;
        const computedTotalHeight = measurementArray.reduce((sum, measurement) => sum + (measurement ?? 0), 0);
        const offsetByIndex = (targetIndex) => {
            let offset = 0;
            for (let i = 0; i < targetIndex; i += 1) {
                offset += measurementArray[i] ?? 0;
            }
            return offset;
        };
        const findStartIndex = () => {
            let accumulated = 0;
            for (let i = 0; i < measurementArray.length; i += 1) {
                const next = accumulated + (measurementArray[i] ?? 0);
                if (next > scrollTop) {
                    return Math.max(0, i - OVERSCAN);
                }
                accumulated = next;
            }
            return 0;
        };
        const startIndex = findStartIndex();
        let dynamicEndIndex = startIndex;
        let accumulatedHeight = 0;
        while (dynamicEndIndex < measurementArray.length &&
            accumulatedHeight < containerHeight) {
            accumulatedHeight += measurementArray[dynamicEndIndex] ?? 0;
            dynamicEndIndex += 1;
        }
        const endIndex = Math.min(measurementArray.length - 1, dynamicEndIndex + OVERSCAN);
        if (comments.length <= 25) {
            const items = comments.map((comment, index) => ({
                index,
                comment,
                top: offsetByIndex(index),
            }));
            return {
                visibleItems: items,
                totalHeight: computedTotalHeight || items.length * 180,
                shouldVirtualize: false,
            };
        }
        const items = [];
        for (let index = startIndex; index <= endIndex; index += 1) {
            const comment = comments[index];
            if (!comment)
                continue;
            const top = offsetByIndex(index);
            items.push({ index, comment, top });
        }
        const fallbackHeight = comments.length * 180;
        return {
            visibleItems: items,
            totalHeight: computedTotalHeight || fallbackHeight,
            shouldVirtualize: true,
        };
    }, [comments, containerHeight, measurements, scrollTop]);
    if (comments.length === 0) {
        return (_jsx("p", { className: "rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500", children: "Aucun commentaire pour le moment." }));
    }
    if (!shouldVirtualize) {
        return (_jsx("div", { className: "space-y-3", children: visibleItems.map(({ comment, index }) => (_jsx("div", { ref: attachMeasurementRef(index), children: _jsx(CommentItem, { comment: comment, currentUserId: currentUserId, onEdit: onEdit, onDelete: onDelete }) }, comment.id))) }));
    }
    return (_jsx("div", { ref: containerRef, className: "relative h-[480px] overflow-y-auto", onScroll: handleScroll, role: "list", "aria-label": "Liste des commentaires", children: _jsx("div", { style: { height: `${totalHeight}px`, position: "relative" }, children: visibleItems.map(({ index, comment, top }) => (_jsx("div", { ref: attachMeasurementRef(index), style: {
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top,
                }, children: _jsx(CommentItem, { comment: comment, currentUserId: currentUserId, onEdit: onEdit, onDelete: onDelete }) }, comment.id))) }) }));
}
